import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Truck, Users, ChevronLeft, ChevronRight, Calendar, UserCheck, UserMinus, Building2,
  List, LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';

// ──────────────────────────────────────────────────────────────────────────────
// Date helpers (locale-safe).  All date math here works on the user's local
// wall-clock so the week strip matches what they see on the calendar on their
// device. We never round-trip through UTC ISO strings for "today".
// ──────────────────────────────────────────────────────────────────────────────
const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromYMD(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function startOfWeekMonday(d) {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // getDay() returns 0=Sun..6=Sat. Monday=1.
  const offset = (out.getDay() + 6) % 7; // days since Monday
  out.setDate(out.getDate() - offset);
  return out;
}

function addDays(d, n) {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + n);
  return out;
}

function formatShort(d) {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────
export default function VisualSchedulePage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;

  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState('');
  const [selectedDate, setSelectedDate] = useState(toYMD(new Date()));
  const [weekAnchor, setWeekAnchor] = useState(() => startOfWeekMonday(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const isOperator = role === 'OPERATOR';
  const canPickFacility = role === 'ADMIN' || role === 'MANAGER';

  // Load facilities scoped to the user (one-shot on mount).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/facilities');
        if (cancelled) return;
        setFacilities(res.data || []);
      } catch (err) {
        toast.error('Failed to load facilities');
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Auto-select once facilities arrive (Operator → only one; Admin/Manager → first).
  useEffect(() => {
    if (!facilityId && facilities.length) {
      setFacilityId(facilities[0].id);
    }
  }, [facilities, facilityId]);

  const fetchData = useCallback(async () => {
    if (!facilityId || !selectedDate) return;
    setLoading(true);
    try {
      const res = await api.get('/work-schedules/visual', {
        params: { facility_id: facilityId, date: selectedDate },
      });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load schedule';
      toast.error(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, facilityId, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Keep the visible week aligned with the selected day so picking a date from
  // anywhere always shows the right strip.
  useEffect(() => {
    setWeekAnchor(startOfWeekMonday(fromYMD(selectedDate)));
  }, [selectedDate]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekAnchor, i));
  }, [weekAnchor]);

  const handlePrevWeek = () => setWeekAnchor((a) => addDays(a, -7));
  const handleNextWeek = () => setWeekAnchor((a) => addDays(a, 7));

  const facilityLabel = useMemo(() => {
    return facilities.find((f) => f.id === facilityId)?.name || '';
  }, [facilities, facilityId]);

  return (
    <div className="space-y-5" data-testid="visual-schedule-page">
      {/* ── TOP BAR ──────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#005EF8]/10 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-[#005EF8]" />
          </div>
          <h1 className="text-2xl font-semibold text-[#323033]">Work Schedules</h1>
        </div>
        {/* Sibling view toggle — Visual is active here; selecting Table
            routes back to the standard /work-schedules list. */}
        <div className="inline-flex items-center gap-2" data-testid="ws-view-toggle">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/work-schedules')}
            data-testid="ws-view-table-btn"
            aria-pressed="false"
          >
            <List className="w-4 h-4 mr-1" />
            Table
          </Button>
          <Button
            variant="default"
            size="sm"
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            data-testid="ws-view-visual-btn"
            aria-pressed="true"
          >
            <LayoutGrid className="w-4 h-4 mr-1" />
            Visual
          </Button>
        </div>
      </div>

      {/* Facility row */}
      <Card className="border-[#E5E5E5]">
        <CardContent className="py-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-[280px]">
            <Building2 className="w-4 h-4 text-[#5A5A5A]" />
            {canPickFacility ? (
              <Select value={facilityId} onValueChange={setFacilityId}>
                <SelectTrigger className="w-full sm:w-72" data-testid="visual-facility-select">
                  <SelectValue placeholder="Select a facility" />
                </SelectTrigger>
                <SelectContent>
                  {facilities.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span
                className="text-sm font-medium text-[#323033]"
                data-testid="visual-facility-label"
              >
                {facilityLabel || '—'}
              </span>
            )}
          </div>

          {/* Week strip */}
          <div className="flex items-center gap-2 overflow-x-auto" data-testid="visual-week-strip">
            <Button
              variant="outline" size="sm"
              onClick={handlePrevWeek}
              data-testid="visual-week-prev"
              aria-label="Previous week"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-stretch gap-1">
              {weekDays.map((d, i) => {
                const ymd = toYMD(d);
                const isSelected = ymd === selectedDate;
                return (
                  <button
                    key={ymd}
                    type="button"
                    onClick={() => setSelectedDate(ymd)}
                    data-testid={`visual-day-${ymd}`}
                    className={`px-3 py-1.5 rounded-md border text-xs font-medium leading-tight text-center transition-colors min-w-[68px] ${
                      isSelected
                        ? 'bg-[#005EF8] border-[#005EF8] text-white shadow-sm'
                        : 'bg-white border-[#E5E5E5] text-[#323033] hover:border-[#005EF8] hover:text-[#005EF8]'
                    }`}
                  >
                    <div>{DOW_LABELS[i]}</div>
                    <div className="font-semibold text-[13px]">{formatShort(d)}</div>
                  </button>
                );
              })}
            </div>
            <Button
              variant="outline" size="sm"
              onClick={handleNextWeek}
              data-testid="visual-week-next"
              aria-label="Next week"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── MAIN LANES ─────────────────────────────────────────── */}
      {loading ? (
        <Card><CardContent className="py-12 text-center text-[#5A5A5A]">Loading schedule…</CardContent></Card>
      ) : !data ? (
        <Card><CardContent className="py-12 text-center text-[#5A5A5A]">No data.</CardContent></Card>
      ) : (
        <>
          <div className="space-y-3" data-testid="visual-lanes">
            {data.trucks.length === 0 && (
              <Card>
                <CardContent className="py-10 text-center text-[#5A5A5A]" data-testid="no-truck-lanes">
                  No truck assignments for this day.
                </CardContent>
              </Card>
            )}
            {data.trucks.map((truck) => (
              <TruckLane key={truck.vehicle_id} truck={truck} />
            ))}

            {data.unassigned_trucks.length > 0 && (
              <Card className="bg-[#FAFAFA] border-dashed border-[#D3D5D4]" data-testid="visual-unassigned-trucks">
                <CardContent className="py-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5A5A5A] mr-2">
                    No Assignment Today
                  </span>
                  {data.unassigned_trucks.map((t) => (
                    <span
                      key={t.vehicle_id}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-[#E5E5E5] text-xs text-[#5A5A5A]"
                      data-testid={`visual-unassigned-${t.vehicle_id}`}
                    >
                      <Truck className="w-3 h-3" /> {t.display_id}
                    </span>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* ── PERSONNEL STATUS ──────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="visual-personnel">
            <PersonnelList
              title="Assigned"
              icon={<UserCheck className="w-4 h-4 text-emerald-700" />}
              people={data.assigned_personnel}
              tone="assigned"
              testId="visual-assigned"
            />
            <PersonnelList
              title="Idle"
              icon={<UserMinus className="w-4 h-4 text-amber-700" />}
              people={data.idle_personnel}
              tone="idle"
              testId="visual-idle"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Truck Lane component
// ──────────────────────────────────────────────────────────────────────────────
const TruckLane = ({ truck }) => {
  const lead = truck.job_lead;
  const crew = truck.crew || [];

  return (
    <Card className="border-[#E5E5E5]" data-testid={`visual-truck-${truck.vehicle_id}`}>
      <CardContent className="p-4 space-y-3">
        {/* Lane header */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-[#005EF8]" />
            <span className="font-bold text-[#323033]" data-testid={`visual-truck-id-${truck.vehicle_id}`}>
              {truck.display_id}
            </span>
          </div>
          {lead && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#005EF8]/10 text-[#005EF8] text-xs font-medium">
              <span className="uppercase tracking-wider text-[10px] font-semibold">Lead</span>
              {lead.name}
            </div>
          )}
          {crew.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-[#5A5A5A]">
              <Users className="w-3.5 h-3.5" />
              {crew.map((c) => (
                <span
                  key={c.id}
                  className="px-2 py-0.5 rounded-full bg-[#F5F5F5] text-[#323033]"
                  data-testid={`visual-crew-${truck.vehicle_id}-${c.id}`}
                >
                  {c.name}
                </span>
              ))}
            </div>
          )}
          {!lead && crew.length === 0 && (
            <span className="text-xs italic text-[#9A9A9A]">No crew assigned</span>
          )}
        </div>

        {/* Site cards */}
        {truck.sites.length === 0 ? (
          <div className="text-xs italic text-[#9A9A9A]">No sites for this truck.</div>
        ) : (
          <div className="flex flex-col md:flex-row gap-3 md:overflow-x-auto md:pb-1">
            {truck.sites.map((site, i) => (
              <div
                key={`${site.repair_site_name}-${i}`}
                className="md:min-w-[220px] md:max-w-[260px] rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] p-3 flex-shrink-0"
                data-testid={`visual-site-${truck.vehicle_id}-${i}`}
              >
                <div className="font-semibold text-[#323033] text-sm" title={site.repair_site_name}>
                  {site.repair_site_name}
                </div>
                {site.cars.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {site.cars.map((c, idx) => (
                      <span
                        key={`${c.label}-${idx}`}
                        className="px-1.5 py-0.5 rounded bg-white border border-[#E5E5E5] text-[11px] text-[#323033]"
                      >
                        {c.label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1.5 text-[11px] italic text-[#9A9A9A]">No car numbers</div>
                )}
                <div className="mt-2 text-[11px] text-[#5A5A5A]">
                  {site.car_count} {site.car_count === 1 ? 'car' : 'cars'}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PersonnelList = ({ title, icon, people, tone, testId }) => {
  const toneClass = tone === 'assigned'
    ? 'bg-emerald-50 border-emerald-100 text-emerald-900'
    : 'bg-amber-50 border-amber-100 text-amber-900';
  return (
    <Card className="border-[#E5E5E5]" data-testid={testId}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold text-[#323033]">{title}</span>
          <span className="text-xs text-[#5A5A5A]">({people.length})</span>
        </div>
        {people.length === 0 ? (
          <div className="text-xs italic text-[#9A9A9A]">None</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {people.map((p) => (
              <span
                key={p.id}
                className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs ${toneClass}`}
                data-testid={`${testId}-${p.id}`}
              >
                {p.name}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
